import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { getDistanceFromLatLonInKm, LatLng } from '@pedix-workspace/utils';
import { AppRequestContextService } from '../../../app-request-context.service';
import { DeliveryService, GeoIpService } from '@pedix-workspace/pedixapp-core-services';
import { GeolocationService } from '@pedix-workspace/angular-ui-map';
import { ModalDialogService } from '@pedix-workspace/angular-ui-modal';
import { GeolocationErrorDialogComponent } from '@pedix-workspace/pedixapp-shared-catalog';
import { environment } from '../../../../environments/environment';
import {
  IconChevronRightComponent,
  IconGeolocationComponent,
  IconMapMarkerComponent,
  IconLoadingComponent,
} from '@pedix-workspace/angular-ui-icons';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { FormsModule } from '@angular/forms';
import { ApiV1_Subsidiary } from '@pedix-workspace/api-v1';
import { ToastrService } from 'ngx-toastr';
import {
  getFirstOpenedSubsidiaryOrDefault,
  getSortedSubsidiariesByGroupAndDistance,
  getSubsidiariesWithoutTemporaryClosure,
  SubsidiariesByGroup,
} from '@pedix-workspace/pedixapp-shared-catalog';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { isPlatformBrowser } from '@angular/common';

const MAX_DISTANCE_FOR_NEAREST_SUBSIDIARY = 15; // km

@Component({
  selector: 'app-subsidiaries-group',
  standalone: true,
  imports: [
    TranslocoDirective,
    IconChevronRightComponent,
    IconGeolocationComponent,
    IconMapMarkerComponent,
    FormsModule,
    IconLoadingComponent,
    ButtonComponent,
  ],
  templateUrl: './subsidiaries-group.component.html',
  styleUrls: ['./subsidiaries-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubsidiariesGroupComponent implements OnInit {
  private readonly appRequestContextService = inject(AppRequestContextService);
  private readonly router = inject(Router);
  private readonly deliveryService = inject(DeliveryService);
  private readonly geolocationService = inject(GeolocationService);
  private readonly modalDialogService = inject(ModalDialogService);
  private readonly toastrService = inject(ToastrService);
  private readonly geoIpService = inject(GeoIpService);
  private readonly transloco = inject(TranslocoService);
  private readonly platformId = inject(PLATFORM_ID);

  readonly loading = signal(true);
  readonly loadingGps = signal(false);
  readonly userLocation = signal<LatLng | null>(null);

  private readonly subsidiaries = computed(() => this.appRequestContextService.subsidiaries());

  readonly showGpsButton = this.geolocationService.isAvailable;

  readonly subsidiariesByGroup = computed(() => {
    const subsidiaries = this.subsidiaries();
    const userLocation = this.userLocation();

    return getSortedSubsidiariesByGroupAndDistance(subsidiaries, userLocation);
  });

  async ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      if (
        !this.appRequestContextService.subsidiarySelectionSource() &&
        (await this.geolocationService.isPermissionGranted())
      ) {
        this.selectByGps(true);
      } else {
        this.retrieveGeoIpLocation();
      }
    }
  }

  async autoSelectByGroup(
    subsidiaryGroup: SubsidiariesByGroup,
    nearestSubsidiary?: ApiV1_Subsidiary,
  ) {
    const bestSubsidiary =
      nearestSubsidiary || getFirstOpenedSubsidiaryOrDefault(subsidiaryGroup.subsidiaries);

    this.loading.set(true);

    // If there is only one subsidiary in the group, it is considered selected by the user
    const isUserSelected = subsidiaryGroup.subsidiaries.length === 1 ? true : false;

    await this.appRequestContextService.switchSubsidiaryCatalog({
      establishmentId: bestSubsidiary.id,
      isUserSelected,
    });

    this.router.navigate(['/']);

    this.loading.set(false);
  }

  async selectByGps(calledFromOnInit = false) {
    this.loadingGps.set(true);

    const geolocationResult = await this.geolocationService.geolocate();

    this.loadingGps.set(false);

    if (geolocationResult) {
      this.userLocation.set(geolocationResult);
    } else if (calledFromOnInit) {
      this.retrieveGeoIpLocation();
      return;
    } else {
      await this.modalDialogService
        .attachDialog(GeolocationErrorDialogComponent)
        .open()
        .catch(() => {});

      return;
    }

    this.loading.set(true);

    const subsidiaries = getSubsidiariesWithoutTemporaryClosure(this.subsidiaries());

    const result =
      subsidiaries.length > 0
        ? await this.deliveryService
            .getMatchingSubsidiaryForAddress({
              apiBaseUrl: environment.apiUrl,
              coordinates: geolocationResult,
              establishmentsIds: subsidiaries.map(s => s.id),
            })
            .catch(error => {
              console.error(error);

              return null;
            })
        : 'not-found';

    // Tries to match subsidiary from address coverage
    if (result && result !== 'not-found') {
      const matchedSubsidiaryId = result.matches[0].establishmentId;

      await this.appRequestContextService.switchSubsidiaryCatalog({
        establishmentId: matchedSubsidiaryId,
        isUserSelected: true,
      });

      this.router.navigate(['/']);
    } else {
      // Buscamos el grupo que contenga la sucursal mas cercana, e invocamos autoSelectByGroup para seleccionar sucursal por defecto
      const nearestSubsidiary = this.findNearestSubsidiary(geolocationResult, subsidiaries);
      const nearestSubsidiaryGroup = nearestSubsidiary.subsidiary
        ? this.subsidiariesByGroup().find(group =>
            group.subsidiaries.find(
              subsidiary => subsidiary.id === nearestSubsidiary.subsidiary.id,
            ),
          )
        : null;

      if (
        nearestSubsidiaryGroup &&
        nearestSubsidiary.distance <= MAX_DISTANCE_FOR_NEAREST_SUBSIDIARY
      ) {
        await this.autoSelectByGroup(nearestSubsidiaryGroup, nearestSubsidiary.subsidiary);
      } else if (!calledFromOnInit) {
        this.toastrService.error(this.transloco.translate('subsidiaries.noNearbyBranches'));
      }
    }
    this.loading.set(false);
  }

  private async retrieveGeoIpLocation() {
    const geoIp = await this.geoIpService.retrieveGeoIpState(environment.workerServicesUrl);

    this.userLocation.set(geoIp);
    this.loading.set(false);
  }

  private findNearestSubsidiary(coordinates: LatLng, subsidiaries: ApiV1_Subsidiary[]) {
    let nearestSubsidiary: ApiV1_Subsidiary | null = null;
    let minDistance = Infinity;

    for (const subsidiary of subsidiaries) {
      if (!subsidiary.addressDetails?.coordinates) {
        continue;
      }

      const distance = getDistanceFromLatLonInKm(
        coordinates,
        subsidiary.addressDetails?.coordinates,
      );

      if (distance < minDistance) {
        minDistance = distance;
        nearestSubsidiary = subsidiary;
      }
    }

    return {
      subsidiary: nearestSubsidiary,
      distance: minDistance,
    };
  }
}
